/*
 * Copyright 2025 Intuition Robotics ltd
 */
import type {DeviceType, Unit} from "./units.js";
import type {MessageType} from "@intuitionrobotics/push-pub-sub";
import type {Base_PushPayload} from "./api.js";

export const AppPackage_Kaspero = "com.ir.kyou.kaspero";
export const AppPackage_PM = "com.ir.pm";
export const AppPackage_ElliQ = "com.ir.ai.kyou";
export const AppPackage_Strickland = "com.ir.ai.strickland";
export const AppPackage_TabletElliQ = "com.ir.remoteScreen.elliq";
export const PmInstallNow = "pm-install-now";
export const OnPair = "on-paired";
export const OnUnPair = "on-unpaired";
export const OnContactsUpdated = "on-contacts-updated";
export const LoadPlayPlanType = "load-and-play-plan";
export const OpenAppReminder = "open-app-reminder";
export const PushKey_NewSuggestedReminderStatus = "suggested-reminder-status-update";
export const PushKey_NewCaregiver = "new-caregiver";
export const NotTakenReminderPushKey = "not-taken-reminder";
export const PushKey_OnNotificationAdded = "on-new-notification-added";
export const RefreshReminderStatusesPushKey = "refresh-reminder-statuses";
export const RefreshGoalStatusesPushKey = "refresh-goal-history";
export const RefreshMemoriesPushKey = "refresh-memories";
export const PackageManager_Install = {type: PmInstallNow};

export type PushVersions = "v1" | "v2"

export type BaseDB_PushRegistry = PushRegistry & {
    uuid: string
};
export type DB_PushRegistry = DB_PushRegistryV1 | DB_PushRegistryV2

export type DB_PushRegistryV1 = BaseDB_PushRegistry & PushWrapperVersionV1
export type DB_PushRegistryV2 = BaseDB_PushRegistry & PushWrapperVersionV2

export type PushWrapperVersion = PushWrapperVersionV1 | PushWrapperVersionV2

export type PushWrapperVersionV1 = {
    version: "v1"
}

export type PushWrapperVersionV2 ={
    packageName: string
    version: "v2"
}

export type PushRegistry = {
    timestamp: number
    deviceId: string
};

export type BaseRegistryWrapper = PushRegistry & {
    mId?: string
}

export type FB_RegistryWrapperV1 = BaseRegistryWrapper & {
    data?: PushData | null
    version: "v1"
}

export type FB_RegistryWrapperV2 = {
    data: string | null
}

export type FB_RegistryWrapper = FB_RegistryWrapperV1 | FB_RegistryWrapperV2

export type DeviceNode = {
    mId: string
    data: PushData
    timestamp: number
};

export type Base_SendPush = {
    userId: string
    data: PushData
    ttl?: number
}

export type Request_SendPushToAndroid = Base_SendPush & {
    deviceId: string
    proxyId?: string
}

export type Request_SendPushToUnits = Base_SendPush & {
    units: Unit[]
    devices?: DeviceType[]
}

export type Request_SendSamePushToUnits = Base_PushPayload & {
    units: Unit[],
    deviceToPackageName: {[key: string]: string}
}

export type Request_SendPushToDevices = Base_SendPush & {
    deviceIds: string[]
}

export type Request_ListNotAck = {
    agentId: string
    packageName: string
    serial: string
    ttl: string
}

export type PushData = {
    packageName: string
    message: string
}

export type MessageForAck = {
    mId: string
    data: PushData
}

export type Response_ListNotAck = {
    messages: MessageForAck[]
}
export type Response_PollV2 = Response_ListNotAck
export type Request_PollV2 = {
    packageName: string
    deviceId: string
    ttl: string
    unitId?: string
    deviceType?: DeviceType
}

export enum ActionResult {
    Queued = "Queued",
    Sent = "Sent",
    Read = "Read",
    Success = "Success",
    Error = "Error",
    Processed = "Processed",
    Cancelled = "Cancelled",
    Expired = "Expired"
}

export type Response_SendPushToAndroid = {
    status: ActionResult
    mId: string
    unitAvailable: boolean
    usedMedium: string
    error?: string
}
export type DevicesPushResponse = {
    [deviceId: string]: Response_SendPushToAndroid
};

export type Response_SendPushToUnits = {
    [product: string]: {
        [unitId: string]: DevicesPushResponse | string
    }
};

export type Request_RegisterPushAndroid = {
    deviceId: string
}

export type Request_RegisterPushAndroidV2 = {
    deviceId: string
    packageName: string
}

export type ResponsePushPoll = {
    encryptedData: string[]
}

export type Request_ListPushAndroid = {
    deviceIds: string[]
    fromTimestamp?: number
}



export type ResultPush = ClientResultPush & {
    serverTimestamp: number
}

export type ClientResultPush = {
    auditBy?: string
    clientTimestamp: number
    output?: string
    result: ActionResult
}

export type Request_GetResultsPush = {
    mIds: string[]
}

export type Response_GetResultsPush = {
    [mId: string]: ResultPush
}

export const callerId = "825f4094-a674-4765-96a7-1ac512c02a71" //Used in ElliQ Connect app

export const Key_MessagingOnInboxUpdated = "messaging--on-inbox-updated";

export const PushMethod_VoipPush = "voip-push";
export const PushMethod_PushMessage = "push-message";
export const PushKey_IncomingVideoCall = "incoming-video-call";
export const PushKey_EndingVideoCall = "ending-video-call";
export const PushKey_PhoneNumber = "phoneNumber";
export const PushKey_Messaging = "messaging";
export const PushKey_MessagingNewMessage = "messaging--new-message";
export const PushKey_Birthday = "birthday";
// Ring doorbell visitor alert pushed to a caregiver's Connect app (carries a visitor photo URL).
export const PushKey_Doorbell = "doorbell-visitor";
export const PushKey_Logout = "logout";
export const PushKey_PushResult = "push-result";
export const UnitNotFoundError = `UnitNotFound`;
export type MessageType_PushResult = MessageType<typeof PushKey_PushResult, { mId: string }, ActionResult>

export type Request_ResultPush = {
    mId: string
    pushResult: ClientResultPush
}

export type NodePath = {
    nodePath: string
}

export type Reponse_RegisterPushAndroidV2 = {
    nodePath: string
}

export type Request_CancelPush = {
    mId: string
    userId: string
}

export type Request_CancelManyPush = {
    deviceIds: string[]
    userId: string
}

